<template>
  <div style="width: 100%; height:100%;">
    <div class="animation-wrapper">
      <div ref="animation"
           class="animation"
           @click="togglePlayPauseInternally()"
           :class="{'blur-on': showOverlay || paused}"
      ></div>

      <v-btn
          @click="togglePlayPauseInternally()"
          color="white"
          icon
          class="center"
          v-if="showOverlay && allMediaReady"
      >
        <v-icon size="100">
          mdi-play-circle-outline
        </v-icon>
      </v-btn>

      <v-chip small class="center elevation-5" v-if="!showOverlay && paused" @click="togglePlayPauseInternally()">
        Animation Paused
      </v-chip>
    </div>
    <audio ref="narration" v-if="narration != null && narration.trim() !== ''"></audio>
    <audio ref="sounds" v-if="sounds != null && sounds.trim() !== ''"></audio>


  </div>
</template>

<script>
  import Lottie from 'lottie-web'

  export default {
    name: "MediaCompositionAnimation",
    props: {
      id: {
        required: true,
      },
      json: {
        type: String,
        required: true,
      },
      narration: {
        type: String,
        required: false,
      },
      sounds: {
        type: String,
        required: false,
      },
      volume: {
        type: Number,
        required: true,
      },
      soundsVolumeRatio: {
        type: Number,
        default: 0.5
      },
      autoplay: { //used to check if play overlay should show
        default: false,
        type: Boolean
      },
      onProgress: {
        type: Function
      }
    },
    data() {
      return {
        duration: 0,
        showOverlay: false,
        animationWidth: 0,
        animationHeight: 0,
        paused: false,
        itemWithLongestDuration: null,
        allMediaReady: false,
        steps: {
          'initAnimation': false,
          'initNarration': false,
          'initSounds': false,
          'calculateItemWithLongestDuration': false
        },
      }
    },
    watch: {
      'volume': function () {
        if (this.$refs.narration)
          this.$refs.narration.volume = this.volume;

        if (this.$refs.sounds)
          this.$refs.sounds.volume = this.volume * this.soundsVolumeRatio;
      },
      'steps': {
        deep: true,
        handler(val) {
          //No need to check again
          if (this.allMediaReady) return;

          let ready = true;
          Object.keys(val).forEach((key) => {
            if (!this.steps[key]) {
              ready = false;
              return;
            }
          });

          if (ready && this.itemWithLongestDuration!=null) {
            this.allMediaReady = true;

            //SET MEDIA DURATION
            if (this.itemWithLongestDuration === 'animation') {
              this._duration = this.getAnimationDuration();
              this.$emit('mediaReady', this.getAnimationDuration());
            } else {
              this._duration = this.$refs[this.itemWithLongestDuration].duration;
              this.$emit('mediaReady', this.$refs[this.itemWithLongestDuration].duration);
            }

            if (!this.autoplay) {
              this.showOverlay = true;
            } else {
              this.$emit('showControls');
            }
          }
        },
      },
    },
    methods: {
      togglePlayPauseInternally() {
        if (this.showOverlay) {
          this.$emit('showControls');
          this.$emit('play');
          this.showOverlay = false;
        } else {
          this.paused ? this.$emit('play') : this.$emit('pause');
        }
      },
      play(viaMediaControls = false) {
        this.showOverlay = false;
        this.animation.play();

        if (!viaMediaControls && this.$refs.narration && this.$refs.narration.currentTime < this.$refs.narration.duration) {
          this.$refs.narration.play().catch(() => {
            this.$emit('pause');
          });
        }

        this.animation.play();

        if (!viaMediaControls && this.$refs.sounds && this.$refs.sounds.currentTime < this.$refs.sounds.duration)
          this.$refs.sounds.play().catch(() => {
            this.$emit('pause');
          });

        this.paused = false;
        this.startUpdatingCurrentTime();

        this.animation.resize();
      },
      pause(viaMediaControls = false) {
        this.animation.pause();

        if (this.$refs.sounds && !viaMediaControls)
          this.$refs.sounds.pause();

        if (this.$refs.narration && !viaMediaControls)
          this.$refs.narration.pause();

        this.paused = true;

      },
      reset() {
        this.paused = false;
        this.animation.stop();

        if (this.$refs.sounds)
          this.$refs.sounds.pause();

        if (this.$refs.narration)
          this.$refs.narration.pause();

        this.$refs.sounds.currentTime = 0;
        this.$refs.narration.currentTime = 0;

        this.showOverlay = true;
      },
      forward(time = 5) {
        //NB: animation works in milliseconds and sounds work in seconds
        let goToTime = 0;

        if (this.itemWithLongestDuration === 'animation') {
          goToTime = this.getCurrentAnimationTime(false) + time;
        } else {
          goToTime = parseFloat(this.$refs[this.itemWithLongestDuration].currentTime + time);
        }

        if (this.$refs.sounds.duration >= goToTime) {
          this.$refs.sounds.currentTime = parseFloat(goToTime);
        } else {
          this.$refs.sounds.currentTime = this.$refs.sounds.duration;
          // this.$refs.sounds.pause();
        }

        if (this.$refs.narration.duration >= goToTime) {
          this.$refs.narration.currentTime = parseFloat(goToTime);
        } else {
          this.$refs.narration.currentTime = this.$refs.narration.duration;
          // this.$refs.narration.pause();
        }

        if (this.getAnimationDuration() < goToTime) {
          goToTime = this.getAnimationDuration();
          this.animation.pause();
        }

        this.paused ? this.animation.goToAndStop(goToTime * 1000) : this.animation.goToAndPlay(goToTime * 1000);

        if (this.onProgress) {
          this.onProgress(this.getMainCurrentTime());
        }

      },
      backward(time = 5) {
        //NB: animation works in milliseconds and sounds work in seconds
        let goToTime = 0;

        if (this.itemWithLongestDuration === 'animation') {
          goToTime = this.getCurrentAnimationTime(false) - time;
        } else {
          goToTime = parseFloat(this.$refs[this.itemWithLongestDuration].currentTime - time);
        }

        if (goToTime < 0) {
          goToTime = 0;
        } else if (goToTime > this._duration) {
          goToTime = this._duration;
        }

        if (this.$refs.sounds != null) {
          if (this.$refs.sounds.duration >= goToTime) {
            this.$refs.sounds.currentTime = goToTime;
            if (!this.paused) this.$refs.sounds.play(); //start playing again if the end was reached
          } else {
            this.$refs.sounds.currentTime = this.$refs.sounds.duration;
          }
        }

        if (this.$refs.narration != null) {
          if (this.$refs.narration.duration >= goToTime) {
            this.$refs.narration.currentTime = goToTime;
            if (!this.paused) this.$refs.narration.play(); //start playing again if the end was reached
          }
        }

        if (this.getAnimationDuration() >= goToTime) {
          this.paused ? this.animation.goToAndStop(goToTime * 1000) : this.animation.goToAndPlay(goToTime * 1000);
        }

        if (this.onProgress) {
          this.onProgress(this.getMainCurrentTime());
        }
      },
      getCurrentAnimationTime(milliseconds = true) {
        let multiplier = milliseconds ? 1000 : 1;
        return (this.animation.currentFrame / this.animation.frameRate) * multiplier;
      },
      getAnimationDuration() {
        return this.animation.getDuration(false);
      },
      initAnimation() {
        this.animation = Lottie.loadAnimation({
          container: this.$refs.animation,
          renderer: 'svg',
          path: this.json,
          autoplay: false,
        });
      },
      initAudio() {
        if (this.narration) {
          this.$refs.narration.volume = this.volume;
          this.$refs.narration.src = this.narration;
        } else {
          this.steps.initNarration = true;
        }

        if (this.sounds) {
          this.$refs.sounds.src = this.sounds;
          this.$refs.sounds.volume = this.volume * this.soundsVolumeRatio;
        } else {
          this.steps.initSounds = true;
        }

        if (this.narration) {
          this.$refs.narration.addEventListener('canplaythrough', () => {
            this.steps.initNarration = true;
          });
        }

        if (this.sounds) {
          this.$refs.sounds.addEventListener('canplaythrough', () => {
            this.steps.initSounds = true;
          });
        }

      },
      calculateItemWithLongestDuration() {

        let arrayOfDurations = [
          {'item': 'animation', 'duration': this.getAnimationDuration()}
        ];

        if (this.$refs.narration)
          arrayOfDurations.push({'item': 'narration', 'duration': this.$refs.narration.duration});

        if (this.$refs.sounds)
          arrayOfDurations.push({'item': 'sounds', 'duration': this.$refs.sounds.duration});

        if(arrayOfDurations.includes(NaN) || arrayOfDurations.includes('NaN')){
          console.log('(A) Had to restart');
          setTimeout(()=>{
            this.calculateItemWithLongestDuration();
          },150);
          return;
        }

        this.itemWithLongestDuration = arrayOfDurations.reduce(function (prev, current) {
          return (prev.duration > current.duration) ? prev : current
        }).item;

        if(!this.itemWithLongestDuration){
          console.log('(B) Had to restart');
          setTimeout(()=>{
            this.calculateItemWithLongestDuration();
          },150);
          return;
        }

        this.steps.calculateItemWithLongestDuration = true;

        this.addEventListeners();
      },
      getMainCurrentTime() {
        // if(!this.itemWithLongestDuration) return 0;
        //this will return the current time for the longest item in the list
        if (this.itemWithLongestDuration === 'animation') {
          return this.getCurrentAnimationTime(false);
        } else {
          return this.$refs[this.itemWithLongestDuration].currentTime;
        }
      },
      addEventListeners(){
        //EMIT WHEN MEDIA COMPLETE
        if (this.itemWithLongestDuration === 'animation') {
          this.animation.addEventListener('complete', () => {
            this.$emit('complete', this.id);
            this.stopUpdatingCurrentTime();
            // this.reset();
          });
        } else {
          return this.$refs[this.itemWithLongestDuration].addEventListener('ended', () => {
            this.$emit('complete', this.id);
            this.stopUpdatingCurrentTime();
            // this.reset();
          });
        }
      },
      startUpdatingCurrentTime() {
        if (this.allMediaReady) {
          this.currentTimeInterval = setInterval(() => {
            if (this.paused) {
              this.stopUpdatingCurrentTime();
            }
            if (this.onProgress) {
              this.onProgress(this.getMainCurrentTime());
            }
          }, 50);
        }
      },
      stopUpdatingCurrentTime() {
        clearInterval(this.currentTimeInterval);
        this.currentTimeInterval = null;
      }
    },
    beforeDestroy() {
      this.stopUpdatingCurrentTime();
      this.animation.destroy();

      if (this.$refs.narration != null) {
        this.$refs.narration.src = '';
        this.$refs.narration.remove();
      }

      if (this.$refs.sounds != null) {
        this.$refs.sounds.src = '';
        this.$refs.sounds.remove();
      }
    },
    mounted() {

      this.initAnimation();
      this.initAudio();


      //ANIMATION IS LOADED INTO THE DOM
      this.animation.addEventListener('DOMLoaded', () => {
        this.animationHeight = this.$refs.animation.clientHeight;
        this.animationWidth = this.$refs.animation.clientWidth;
        this.steps.initAnimation = true;
        setTimeout(()=>{
          this.calculateItemWithLongestDuration();
        },150);
      });


      if (navigator != null && ('mediaSession' in navigator)) {

        navigator.mediaSession.setActionHandler('play', () => {
          this.$emit('play');
        });

        navigator.mediaSession.setActionHandler('pause', () => {
          this.$emit('pause');
        });

        navigator.mediaSession.setActionHandler('previoustrack', () => {
          this.backward();
        });

        navigator.mediaSession.setActionHandler('nexttrack', () => {
          this.forward();
        });

        navigator.mediaSession.setActionHandler('seekbackward', () => {
          this.backward();
        });

        navigator.mediaSession.setActionHandler('seekforward', () => {
          this.forward();
        });
      }
    },
  }
</script>

<style scoped>
  .blur-on, .blur-element {
    -webkit-filter: blur(3px);
    -moz-filter: blur(3px);
    -o-filter: blur(3px);
    -ms-filter: blur(3px);
    filter: blur(3px);
  }

  .animation-wrapper {
    position: relative;
    height: 100%
  }

  .animation-wrapper > .animation {
    width: 100%;
    height: 100%;
    vertical-align: middle;
  }

  .center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

</style>
